<template>
  <q-btn
    :disable="(options.length === 0 && !optionsFunction) || disable"
    :bg-color="!$q.dark.isActive ? 'white' : 'black'"
    no-caps
    color="grey-5"
    outline
    class="q-py-sm"
    data-html2canvas-ignore
  >
    <div class="button-content" :class="!$q.dark.isActive ? 'text-black' : 'text-white'">
      {{ fullLabel }}
    </div>
    <q-menu transition-duration="0" @before-hide="beforeHide" @before-show="loadOptions">
      <q-card>
        <div class="title q-px-md q-pt-md">{{ fullLabel }}</div>
        <div v-if="loading" class="q-pa-md">
          <q-linear-progress indeterminate color="primary" />
        </div>
        <q-markup-table v-else flat dense class="no-padding">
          <tr>
            <td></td>
            <td></td>
            <td v-for="(c, idx) in columns" :key="idx" class="text-center">
              {{ c.label }}
            </td>
          </tr>
          <tr v-if="loadedOptions.length > 1">
            <td></td>
            <td></td>
            <td v-for="(c, idx) in columns" :key="idx" class="text-center">
              <template v-if="idx === 0">
                <q-checkbox
                  :model-value="allValuesAreChecked"
                  keep-color
                  :color="$q.dark.isActive ? 'grey-7' : 'grey-5'"
                  dense
                  @click="allValuesAreChecked ? setNone() : setAll()"
                />
              </template>
              <template v-if="$slots['extra-column-checkbox-1'] && idx === 1">
                <slot name="extra-column-checkbox-1" />
              </template>
              <template v-if="$slots['extra-column-checkbox-2'] && idx === 2">
                <slot name="extra-column-checkbox-2" />
              </template>
            </td>
          </tr>
          <tr v-if="loadedOptions.length === 0">
            <td :colspan="columns.length + 2" class="text-center">No options available</td>
          </tr>
          <tr v-for="option in loadedOptions" :key="option.value" class="button-trigger">
            <td class="flex">
              <div
                style="max-width: 200px; text-overflow: ellipsis; overflow: hidden"
                :style="{
                  borderLeft: option.color ? `4px solid ${option.color}` : 'none',
                  paddingLeft: option.color ? '8px' : '0'
                }"
                class="no-wrap"
              >
                {{ option.label }}
              </div>
              <span v-if="option.count" class="text-grey-7 q-ml-xs">({{ option.count }})</span>
            </td>
            <td>
              <q-btn
                v-if="groupedSelectionsKey"
                flat
                dense
                color="primary"
                class="visible-on-hover q-ml-md q-mr-sm q-my-none"
                style="min-width: 40px; font-size: 12px; line-height: 1"
                @click="selectAllInGroup(option)"
              >
                All {{ option.value[groupedSelectionsKey] }}{{ groupedSelectionsLabel }}
              </q-btn>
              <q-btn
                flat
                dense
                color="primary"
                class="visible-on-hover q-ml-md q-mr-sm q-my-none"
                style="min-width: 40px; font-size: 12px; line-height: 1"
                @click="selectOnly(option.label)"
              >
                Only
              </q-btn>
            </td>
            <td class="text-center">
              <q-checkbox
                v-model="selectedOptions"
                dense
                :val="option.label"
                @update:model-value="useAutoSubmit"
              />
            </td>
            <td v-if="$slots['extra-column-1']" class="text-center">
              <slot name="extra-column-1" :option="option" />
            </td>
            <td v-if="$slots['extra-column-2']" class="text-center">
              <slot name="extra-column-2" :option="option" />
            </td>
          </tr>
        </q-markup-table>
        <div class="card-actions">
          <q-separator />
          <q-card-actions align="right">
            <q-btn unelevated dense label="None" no-caps @click="setNone" />
            <q-btn unelevated dense label="All" no-caps @click="setAll" />
            <q-btn
              v-close-popup
              unelevated
              dense
              label="Apply"
              color="primary"
              no-caps
              :disable="!isValid"
              @click="submit"
            />
          </q-card-actions>
        </div>
      </q-card>
    </q-menu>
  </q-btn>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { Option } from 'common'

export default {
  name: 'SelectOptions',
  props: {
    label: {
      type: String,
      required: true
    },
    modelValue: {
      type: Array as PropType<Option[] | null>,
      description: 'The selected options',
      default: null
    },
    options: {
      type: Array as PropType<Option[]>,
      required: false,
      default: () => []
    },
    optionsFunction: {
      type: Function as PropType<() => Option[]>,
      required: false,
      default: null
    },
    noEmpty: {
      type: Boolean,
      description: 'If true, the user cannot deselect all options',
      default: false
    },
    autoSubmit: {
      type: Boolean,
      description: 'If true, the component will submit automatically when an option is selected',
      default: false
    },
    columns: {
      type: Array as PropType<{ label: string; key: string }[]>,
      description:
        'Displays extra columns for additional selections. ' +
        'There are slots for extra-columns that can be used to enable all, disable all, and what the row should display.',
      default: () => [],
      required: false
    },
    groupedSelectionsKey: {
      type: String as PropType<string | null>,
      required: false,
      description:
        'Will add a button that will select alike values based on this key. Key must be a nested under value',
      default: null
    },
    groupedSelectionsLabel: {
      type: String as PropType<string>,
      required: false,
      description: 'The label used on the button to group selections together',
      default: ''
    },
    disable: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['update:modelValue', 'disable-option', 'enable-option'],
  data() {
    return {
      selectedOptions: this.modelValue
        ? (this.modelValue.map((option) => option.label) as string[])
        : [],
      loadedOptions: [] as Option[],
      loading: false
    }
  },
  computed: {
    fullLabel() {
      const value = this.modelValue
      if (value === null || this.loadedOptions.length === 0) {
        return this.label
      }
      if (value !== null && (value.length === this.loadedOptions.length || value.length === 0)) {
        return this.label
      }
      return `${this.label} (${value.length})`
    },
    isValid() {
      return this.selectedOptions.length > 0 || !this.noEmpty
    },
    allValuesAreChecked() {
      return (
        this.loadedOptions.length > 0 && this.selectedOptions.length === this.loadedOptions.length
      )
    }
  },
  watch: {
    modelValue(newValue: Option[] | null) {
      if (newValue === null) {
        this.selectedOptions = []
      } else {
        this.selectedOptions = newValue.map((option) => option.label)
      }
    },
    options: {
      handler(newOptions: Option[]) {
        if (newOptions.length > 0 && !this.optionsFunction) {
          this.loadedOptions = newOptions
        }
      },
      immediate: true
    }
  },
  methods: {
    loadOptions() {
      if (this.loadedOptions.length > 0) {
        return
      }
      if (this.optionsFunction) {
        this.loading = true
        setTimeout(() => {
          this.loadedOptions = this.optionsFunction()
          this.loading = false
        }, 100)
      } else if (this.options.length > 0) {
        this.loadedOptions = this.options
      }
    },
    beforeHide() {
      if (this.isValid) {
        this.submit()
      } else {
        this.selectedOptions = this.modelValue ? this.modelValue.map((option) => option.label) : []
      }
    },
    useAutoSubmit() {
      if (this.autoSubmit && this.isValid) {
        this.submit()
      }
    },
    selectOnly(label: string) {
      this.selectedOptions = [label]
      this.submit()
    },
    setAll() {
      this.selectedOptions = this.loadedOptions.map((option) => option.label)
      this.submit()
    },
    selectAllInGroup(optionToGroupBy: Option) {
      const groupedSelectionsKey = this.groupedSelectionsKey
      if (!groupedSelectionsKey) {
        return
      }
      this.selectedOptions = this.loadedOptions
        .filter(
          (option) =>
            option.value[groupedSelectionsKey] === optionToGroupBy.value[groupedSelectionsKey]
        )
        .map((option) => option.label)
      this.submit()
    },
    setNone() {
      this.selectedOptions = []
      if (!this.noEmpty) {
        this.submit()
      }
    },
    submit() {
      // if no change, do nothing
      const value = this.modelValue
      if (
        value !== null &&
        this.selectedOptions.length === value.length &&
        this.selectedOptions.every((selected) => value.some((option) => option.label === selected))
      ) {
        return
      }
      const newValues = this.loadedOptions.filter((option) =>
        this.selectedOptions.some((selected) => selected === option.label)
      )

      if (value) {
        value
          .filter(
            (oldOption) => !newValues.some((newOption) => newOption.label === oldOption.label)
          )
          .forEach((o) => this.$emit('disable-option', o))
      }

      newValues
        .filter(
          (newOption) =>
            value === null || !value.some((oldOption) => oldOption.label === newOption.label)
        )
        .forEach((o) => this.$emit('enable-option', o))
      this.$emit('update:modelValue', newValues)
    }
  }
}
</script>
<style scoped>
.button-content {
  font-weight: 400;
  font-size: small;
}
.title {
  font-size: 17px;
  font-weight: 500;
}
.visible-on-hover {
  opacity: 0;
}
.button-trigger:hover .visible-on-hover {
  opacity: 1;
}
.card-actions {
  background-color: inherit;
  position: sticky;
  bottom: 0;
}
</style>
